import { z } from 'zod';
/**
 * Permissive http(s) URL match. We intentionally don't require an empty
 * path because some Cap deployments live behind a sub-path
 * (e.g. `https://example.com/cap`). Trailing slash is stripped by the
 * outer transform so downstream string concatenation
 * (`${instanceUrl}/${siteKey}/`) is unambiguous.
 */
const URL_PATTERN = /^https?:\/\/[^/\s][^\s]*$/;
/**
 * Body for `PUT /v1/admin/captcha`.
 *
 *   - `siteSecret` is optional on every PUT — when omitted, the existing
 *     ciphertext is preserved (so admins can flip `enabled` without
 *     re-pasting the secret). Whatever the admin enters is the actual
 *     site secret from their Cap dashboard, NOT the dashboard admin key.
 *
 *   - `instanceUrl` and `siteKey` are validated as non-empty / well-formed
 *     ONLY when `enabled = true`. With captcha turned off the values are
 *     never used by the runtime, so requiring them just to save a
 *     "disabled" state would be hostile to first-time setup and to admins
 *     who want to clear a bad config.
 */
export const AdminCaptchaSettingsInput = z
    .object({
    enabled: z.boolean(),
    instanceUrl: z.string().trim().max(2048).default(''),
    siteKey: z.string().trim().max(256).default(''),
    siteSecret: z.string().trim().min(1).max(1024).optional(),
})
    .superRefine((data, ctx) => {
    if (!data.enabled)
        return;
    if (!data.instanceUrl) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['instanceUrl'],
            message: 'required when captcha is enabled',
        });
    }
    else if (!URL_PATTERN.test(data.instanceUrl)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['instanceUrl'],
            message: 'must be an http(s) URL',
        });
    }
    if (!data.siteKey) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['siteKey'],
            message: 'required when captcha is enabled',
        });
    }
})
    .transform((data) => ({
    ...data,
    instanceUrl: data.instanceUrl.replace(/\/+$/, ''),
}));
/**
 * What `GET /v1/admin/captcha` returns. The plaintext site secret is
 * NEVER returned; instead `siteSecretConfigured` is a boolean the admin
 * UI uses to render a "Configured" badge or a "Leave blank to keep
 * existing" placeholder. Mirrors the Telegram bot DTO pattern.
 */
export const AdminCaptchaSettingsDTO = z.object({
    enabled: z.boolean(),
    instanceUrl: z.string(),
    siteKey: z.string(),
    siteSecretConfigured: z.boolean(),
    updatedBy: z.string().uuid().nullable(),
    updatedAt: z.string(),
});
/**
 * What the unauthenticated `GET /v1/auth/captcha-config` returns. Used by
 * the signin/signup pages to decide whether to load and pre-solve a Cap
 * captcha challenge in the browser. When `enabled = false`, the URL/key
 * fields are empty strings — never include the site secret here.
 */
export const PublicCaptchaConfigDTO = z.object({
    enabled: z.boolean(),
    instanceUrl: z.string(),
    siteKey: z.string(),
});
//# sourceMappingURL=captcha.js.map